import { windowIndexAtom } from '@heidi/session/data';

import { type DictationSource } from '@/components/constants';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useMemo } from 'react';

export type DictationStatus = {
  isDictating: boolean;
  isProcessing?: boolean;
  tabName?: DictationSource;
  shouldInformTab?: boolean;
  sessionId?: string;
  startedAt?: Date | null;
};

const defaultDictationStatus: DictationStatus = {
  isDictating: false,
  isProcessing: false,
  shouldInformTab: false,
  startedAt: null,
};

/**
 * Branded type for dictation editor IDs.
 * This prevents arbitrary strings from being used as editor IDs.
 * IDs must be created via `createDictationEditorId()`.
 */
declare const __brand: unique symbol;
type Brand<T, B> = T & { readonly [__brand]: B };

/**
 * Unique identifier for a dictation editor instance.
 * Format: `{windowIndex}-{tabType}` or `{windowIndex}-{tabType}-{documentId}` for documents
 * Examples: "0-note", "1-document-abc123", "0-context"
 *
 * This is a branded type - you cannot assign arbitrary strings to it.
 * Use `createDictationEditorId()` to create valid IDs.
 */
export type DictationEditorId = Brand<string, 'DictationEditorId'>;

/**
 * Creates a unique editor ID for dictation state tracking.
 * This is the ONLY way to create a valid DictationEditorId.
 *
 * @param windowIndex - The split screen window index (0 or 1)
 * @param source - The tab type (note, document, context, askAi, etc.)
 * @param documentId - Optional document ID for document tabs
 * @returns A branded DictationEditorId that can be used with sectionDictateStatusAtomFamily
 */
export const createDictationEditorId = (
  windowIndex: number | undefined,
  source: DictationSource | undefined,
  documentId?: string
): DictationEditorId => {
  const windowPart = windowIndex ?? 0;
  const sourcePart = source ?? 'unknown';
  const id = documentId
    ? `${windowPart}-${sourcePart}-${documentId}`
    : `${windowPart}-${sourcePart}`;
  return id as DictationEditorId;
};

export const sectionDictateStatusAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_editorId: DictationEditorId) => atom<DictationStatus>(defaultDictationStatus)
);

/**
 * @deprecated Use sectionDictateStatusAtomFamily instead for per-editor state tracking.
 * This atom is kept for backwards compatibility during migration.
 */
export const sectionDictateStatusAtom = atom<DictationStatus>(defaultDictationStatus);

/**
 * True while any editor is actively dictating or post-processing, read from the
 * legacy global status atom (kept current by `updateDictationAtomStatus`). Used
 * to lock tab navigation during dictation (V1 and V2 tab strips).
 */
export const useIsAnyDictationActive = () => {
  const status = useAtomValue(sectionDictateStatusAtom);
  return !!status.isDictating || !!status.isProcessing;
};

/**
 * Hook to get dictation status for a specific editor.
 * Automatically gets windowIndex from context.
 * @param source - The tab type (note, document, context, etc.)
 * @param documentId - Optional document ID for document tabs
 */
export const useDictationStatus = (source: DictationSource, documentId?: string) => {
  const windowIndex = useAtomValue(windowIndexAtom);
  const editorId = useMemo(
    () => createDictationEditorId(windowIndex, source, documentId),
    [windowIndex, source, documentId]
  );
  const dictateStatusAtom = useMemo(
    () => sectionDictateStatusAtomFamily(editorId),
    [editorId]
  );
  const dictateStatus = useAtomValue(dictateStatusAtom);

  return {
    isDictating: !!dictateStatus.isDictating,
    isProcessing: !!dictateStatus.isProcessing,
    isSectionDictating: !!dictateStatus.isDictating || !!dictateStatus.isProcessing,
    editorId,
  };
};
